<script setup>
import { ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';

const props = defineProps({
  latitude: {
    type: Number,
    default: null,
  },
  longitude: {
    type: Number,
    default: null,
  },
  bounds: {
    type: Array,
    default: null,
  },
  zoom: {
    type: Number,
    default: 15,
  },
  zoomBias: {
    type: Number,
    default: 0,
  },
  // 'country' | 'city' | 'address' — fit+bias only when this changes
  zoomLevel: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['update:latitude', 'update:longitude']);

const mapContainer = ref(null);

// Leaflet instances (lazy-loaded)
let L = null;
let map = null;
let marker = null;
let pendingView = null;
let lastFitLevel = null;

const placeMarker = (lat, lng) => {
  if (!L || !map) return;

  if (marker) {
    marker.setLatLng([lat, lng]);
  } else {
    marker = L.marker([lat, lng], { draggable: true }).addTo(map);
    marker.on('dragend', () => {
      const pos = marker.getLatLng();
      emit('update:latitude', Math.round(pos.lat * 1e6) / 1e6);
      emit('update:longitude', Math.round(pos.lng * 1e6) / 1e6);
    });
  }
};

const hasBounds = bounds =>
  Array.isArray(bounds) &&
  bounds.length === 2 &&
  Array.isArray(bounds[0]) &&
  Array.isArray(bounds[1]);

const fitToBounds = (
  bounds,
  { lat, lng, bias = props.zoomBias, level = props.zoomLevel } = {}
) => {
  if (!map || !hasBounds(bounds)) return false;

  // Same specificity (e.g. editing city again) — pan only, don't re-zoom
  if (level && level === lastFitLevel) {
    if (lat != null && lng != null) {
      map.panTo([lat, lng]);
    }
    return true;
  }

  map.fitBounds(bounds, { padding: [24, 24], maxZoom: 18 });
  const nextZoom = Math.min(18, Math.max(3, map.getZoom() + (bias || 0)));
  if (lat != null && lng != null) {
    map.setView([lat, lng], nextZoom);
  } else if (bias) {
    map.setZoom(nextZoom);
  }
  lastFitLevel = level || null;
  return true;
};

const applyView = ({ lat, lng, bounds, zoom, zoomBias, zoomLevel } = {}) => {
  const nextLat = lat ?? props.latitude;
  const nextLng = lng ?? props.longitude;
  const nextBounds = bounds !== undefined ? bounds : props.bounds;
  const nextZoom = zoom ?? props.zoom;
  const nextBias = zoomBias ?? props.zoomBias;
  const nextLevel = zoomLevel !== undefined ? zoomLevel : props.zoomLevel;

  if (nextLat == null || nextLng == null) return;

  if (!map) {
    pendingView = {
      lat: nextLat,
      lng: nextLng,
      bounds: nextBounds,
      zoom: nextZoom,
      zoomBias: nextBias,
      zoomLevel: nextLevel,
    };
    return;
  }

  placeMarker(nextLat, nextLng);
  if (
    !fitToBounds(nextBounds, {
      lat: nextLat,
      lng: nextLng,
      bias: nextBias,
      level: nextLevel,
    })
  ) {
    map.setView([nextLat, nextLng], nextZoom);
  }
  map.invalidateSize();
  pendingView = null;
};

const initMap = async () => {
  const [leafletModule] = await Promise.all([
    import('leaflet'),
    import('leaflet/dist/leaflet.css'),
  ]);
  L = leafletModule.default || leafletModule;

  await nextTick();
  if (!mapContainer.value) return;

  const hasCoords = props.latitude != null && props.longitude != null;

  map = L.map(mapContainer.value, {
    center: hasCoords ? [props.latitude, props.longitude] : [50.5, 4.5],
    zoom: hasCoords ? props.zoom : 5,
    zoomControl: true,
  });

  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '&copy; OpenStreetMap contributors',
    maxZoom: 19,
  }).addTo(map);

  if (pendingView) {
    applyView(pendingView);
  } else if (hasCoords) {
    applyView({
      lat: props.latitude,
      lng: props.longitude,
      bounds: props.bounds,
      zoom: props.zoom,
      zoomBias: props.zoomBias,
      zoomLevel: props.zoomLevel,
    });
  } else {
    map.invalidateSize();
  }
};

watch(
  () => [props.bounds, props.zoomBias, props.zoomLevel],
  ([bounds]) => {
    if (props.latitude == null || props.longitude == null) return;
    // Bounds cleared (e.g. pin drag) — allow a fresh fit next geocode
    if (!hasBounds(bounds)) {
      lastFitLevel = null;
      return;
    }
    applyView({
      lat: props.latitude,
      lng: props.longitude,
      bounds,
      zoom: props.zoom,
      zoomBias: props.zoomBias,
      zoomLevel: props.zoomLevel,
    });
  },
  { deep: true }
);

watch(
  () => [props.latitude, props.longitude],
  ([lat, lng]) => {
    if (lat == null || lng == null || !map) return;
    placeMarker(lat, lng);
    // Don't refit bounds on pin drag — only move the marker
    if (!hasBounds(props.bounds)) {
      lastFitLevel = null;
      map.setView([lat, lng], props.zoom);
    }
    map.invalidateSize();
  }
);

onMounted(() => {
  setTimeout(() => initMap(), 50);
});

onBeforeUnmount(() => {
  if (map) {
    map.remove();
    map = null;
    marker = null;
  }
  pendingView = null;
  lastFitLevel = null;
});
</script>

<template>
  <div
    ref="mapContainer"
    class="venue-map-container w-full h-52 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden"
  />
</template>

<style scoped>
.venue-map-container :deep(a) {
  padding: 0 !important;
}

.venue-map-container :deep(img) {
  max-width: none !important;
  max-height: none !important;
}

.venue-map-container :deep(.leaflet-control a) {
  background-color: #fff;
  color: #333;
  text-decoration: none;
  cursor: pointer;
}

.venue-map-container :deep(.leaflet-control-zoom a) {
  width: 26px;
  height: 26px;
  line-height: 26px;
  font-size: 14px;
  font-weight: 700;
  text-align: center;
  display: block;
}

.venue-map-container :deep(.leaflet-control) {
  border: 2px solid rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  background-clip: padding-box;
}

.venue-map-container :deep(.leaflet-control-zoom a + a) {
  border-top: 1px solid #ccc;
}

.venue-map-container :deep(svg) {
  display: inline;
  overflow: hidden;
}
</style>
